import { lazy, Suspense, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import articles from "../articles";
import ArticleEngagement from "./ArticleEngagement";
import "../styles/article-page.css";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const Content = useMemo(
    () => (article ? lazy(article.loadContent) : null),
    [article]
  );

  if (!article) return <div className="py-5 article-page">Article not found</div>;

  const { title, date, by, cover_image, topics, summary, category } = article;
  const metaDescription =
    summary ||
    (topics && topics.length ? `${title} | Topics: ${topics.join(", ")}` : `${title} by ${by}`) ||
    "Article by Mohamed Gad on AI, operations, and venture-building.";
  // Force social previews to use personal headshot instead of per-article images
  const metaImage = "https://www.mohamedgad.com/mohamed-gad.png";
  const metaUrl = `https://www.mohamedgad.com/articles/${slug || ""}`;
  const publishedDate = date ? new Date(date).toISOString() : undefined;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": metaDescription,
    "image": metaImage,
    "datePublished": publishedDate,
    "dateModified": publishedDate,
    "author": {
      "@type": "Person",
      "name": by || "Mohamed Gad",
      "url": "https://www.mohamedgad.com/aboutme"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Mohamed Gad",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.mohamedgad.com/mohamed-gad.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": metaUrl
    },
    "articleSection": category,
    "keywords": [category, ...(topics || [])].filter(Boolean).join(", ")
  };

  return (
    <article className="py-5 article-page">
      <Helmet>
        <title>{title || "Article"} | Mohamed Gad</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={title || "Article"} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={metaUrl} />
        {publishedDate ? <meta property="article:published_time" content={publishedDate} /> : null}
        {publishedDate ? <meta property="article:modified_time" content={publishedDate} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title || "Article"} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <header className="article-page__header">
        {cover_image && (
          <img
            src={cover_image}
            srcSet={article.cover_image_srcset}
            sizes="(max-width: 1080px) 100vw, 1020px"
            className="img-fluid rounded mb-4 article-page__image"
            alt={title}
            width="1280"
            height="720"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        )}
        <h1 className="display-5 fw-bold mb-2">{title}</h1>
        <p className="display-7 mb-2">By: {by}</p>
        <p className="text-muted mb-4">{new Date(date).toLocaleDateString()}</p>
      </header>

      <div className="article-page__content">
        <Suspense
          fallback={
            <div className="article-page__loading" role="status">
              Loading article…
            </div>
          }
        >
          <Content />
        </Suspense>
      </div>

      <ArticleEngagement slug={slug} title={title} />
    </article>
  );
};

export default ArticlePage;
